import { create } from 'zustand';
import { type CompanyInfo, type User, authApi, companyApi } from './api';
import { queryClient } from './query-client';

interface AuthState {
  user: User | null;
  currentCompanyId: string | null;
  availableCompanies: CompanyInfo[];
  loading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
  updateProfile: (displayName: string) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  uploadAvatar: (file: File) => Promise<{ avatarUrl: string }>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  currentCompanyId: null,
  availableCompanies: [],
  loading: false,
  error: null,

  async login(email, password) {
    set({ loading: true, error: null });
    try {
      const res = await authApi.login({ email, password });
      set({
        user: res.user,
        currentCompanyId: res.session.currentCompanyId,
        loading: false,
      });
      // 立即拉 /me 拿完整公司列表
      await get().fetchMe();
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : 'Login failed' });
      throw e;
    }
  },

  async register(email, password, displayName) {
    set({ loading: true, error: null });
    try {
      const res = await authApi.register({ email, password, displayName });
      set({ user: res.user, loading: false });
      // 注册后自动登录
      await get().login(email, password);
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : 'Register failed' });
      throw e;
    }
  },

  async logout() {
    try {
      await authApi.logout();
    } finally {
      set({ user: null, currentCompanyId: null, availableCompanies: [] });
    }
  },

  async fetchMe() {
    try {
      const me = await authApi.me();
      set({
        user: me.user,
        currentCompanyId: me.currentCompanyId,
        availableCompanies: me.availableCompanies,
      });
    } catch (e) {
      // v1.26 bug fix: 之前 catch 一律 set user null, 任何 5xx / 网络错误都把
      // 已登录用户踢回 /login, 表现为"打开 /inquiries 立刻跳登录".
      // 修复: 只在 401 (session 失效) 时清 user, 其他错误保留 user + log.
      const status =
        typeof e === 'object' && e !== null && 'status' in e
          ? (e as { status: unknown }).status
          : undefined;
      if (status === 401) {
        set({ user: null, currentCompanyId: null, availableCompanies: [] });
        console.warn('[fetchMe] session expired, cleared user');
      } else {
        console.error('[fetchMe] non-401 error, keeping existing user:', e);
      }
    }
  },

  async switchCompany(companyId) {
    set({ loading: true });
    try {
      await companyApi.switch(companyId);
      set({ currentCompanyId: companyId, loading: false });
      // v1.37.9: 必须清 react-query 缓存 — 9 处 useQuery 的 queryKey 不含 companyId
      // (['finance','ar'] / ['pipeline'] / ['order',id] / ['quote',id] / ['inquiry',id] …),
      // 不清的话切完公司页面还显示上一个公司的财务/订单数据 (跨租户串数据).
      queryClient.clear();
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : 'Switch failed' });
      throw e;
    }
  },

  async updateProfile(displayName) {
    const res = await authApi.updateProfile({ displayName });
    set({ user: res.user });
  },

  async updatePassword(currentPassword, newPassword) {
    await authApi.updatePassword({ currentPassword, newPassword });
  },

  // v1.28.3: 上传头像, 成功后更新 user.avatarUrl (UserMenu 立刻显示)
  async uploadAvatar(file) {
    const res = await authApi.uploadAvatar(file);
    const cur = get().user;
    if (cur) set({ user: { ...cur, avatarUrl: res.avatarUrl } });
    return res;
  },
}));
